import React, { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import DateField from '../../../../../Components/Form/components/DateField';
import SelectField from '../../../../../Components/Form/components/SelectField';
import {
	BIRTH_METHOD_OPTIONS,
	GENDER_RADIO_OPTIONS,
} from '../../../../HealthData/constants/common';
import NumberField from '../../../../../Components/Form/components/NumberField';
import RadioListField from '../../../../../Components/Form/components/RadioListField';
import FormCondition from '../../../../../Components/Form/FormCondition';
import { PREGNANCY_COURSE } from '../CreatePlanSections/PreviousPregnancyForm';
import FormContainer from '../../../../../Components/Form/FormContainer';
import InputField from '../../../../../Components/Form/components/InputField';
import TextAreaField from '../../../../../Components/Form/components/TextAreaField';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import ScrollContainer from '../../../../Base/Components/ScrollContainer';
import useTranslation from '../../../../../Hooks/useTranslation';
//@ts-ignore
import styles from './EndPlanSection.module.scss';
import {
	HealthPlanUser,
	SubmitQuestionnaireResultMutation,
	useEndHealthPlanUserMutation,
	useSubmitQuestionnaireResultMutation,
} from '../../../../../graphql/graphql';
import useNotification from '../../../../../Hooks/useNotification.hook';
import { removeKeys } from '../../../../../Services/LocalStorage';
import { useModalContext } from '../../../../Modal/Services/ModalContext';
import { TreatmentFormModalContextKeys } from '../../../../../Components/Treatment/TreatmentFormModalContextKeys';
interface Props {
	patientId: string;
	healthPlanUser: HealthPlanUser;
	onSuccess: () => void;
}

const VALIDATION_TRANSLATION_PREFIX = 'validation.endDateSection';

const getValidationSchema = (t: (key: string) => string | undefined) =>
	yup.object({
		birthDate: yup
			.date()
			.transform((curr, orig) => (orig === '' ? null : curr))
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.date`)),
		birthMethod: yup
			.object()
			.test(
				'object-check',
				t(`${VALIDATION_TRANSLATION_PREFIX}.birthMethod`) ?? '',
				value => !isEmpty(value),
			),
		birthWeek: yup
			.string()
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.birthWeek`)),
		newbornWeight: yup
			.string()
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.newbornWeight`)),
		newbornHeight: yup.string().nullable().notRequired(),
		courseOfChildBirthBaby: yup
			.string()
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.courseOfChildBirthBaby`)),
		courseOfChildBirthBabyComplications: yup
			.string()
			.when('courseOfChildBirthBaby', {
				is: (courseOfChildBirthBaby: PREGNANCY_COURSE) =>
					courseOfChildBirthBaby === PREGNANCY_COURSE.COMPLICATED,
				then: yup
					.string()
					.nullable()
					.required(
						t(`${VALIDATION_TRANSLATION_PREFIX}.complicationsDescription`),
					),
			}),
		courseOfChildBirthMother: yup
			.string()
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.courseOfChildBirthMother`)),
		birthOfChild: yup
			.string()
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.birthOfChild`)),
		courseOfChildBirthMotherComplications: yup
			.string()
			.when('courseOfChildBirthMother', {
				is: (courseOfChildBirthMother: PREGNANCY_COURSE) =>
					courseOfChildBirthMother === PREGNANCY_COURSE.COMPLICATED,
				then: yup
					.string()
					.nullable()
					.required(
						t(`${VALIDATION_TRANSLATION_PREFIX}.complicationsDescription`),
					),
			}),
		birthPlace: yup
			.string()
			.nullable()
			.required(t(`${VALIDATION_TRANSLATION_PREFIX}.birthPlace`)),
	});

const APGAR_VALUES = [
	{
		value: '0',
		label: '0',
	},
	{
		value: '1',
		label: '1',
	},
	{
		value: '2',
		label: '2',
	},
	{
		value: '3',
		label: '3',
	},
	{
		value: '4',
		label: '4',
	},
	{
		value: '5',
		label: '5',
	},
	{
		value: '6',
		label: '6',
	},
	{
		value: '7',
		label: '7',
	},
	{
		value: '8',
		label: '8',
	},
	{
		value: '9',
		label: '9',
	},
	{
		value: '10',
		label: '10',
	},
];

const EndPlanSection: React.FC<Props> = ({
	onSuccess,
	patientId,
	healthPlanUser,
}) => {
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const [submitQuestionnaireResult] = useSubmitQuestionnaireResultMutation();
	const [endHealthPlanUser] = useEndHealthPlanUserMutation();
	const { showNotificationSuccess } = useNotification();

	const formMethods = useForm({
		defaultValues: {}, // TODO needs to be set otherwise it will not work
		resolver: yupResolver(getValidationSchema(t)),
	});

	const { registerFormByKey, unregisterFormsByKey } = useModalContext();

	const handleSubmit = async (values: any) => {
		setIsLoading(true);

		const result: { data?: SubmitQuestionnaireResultMutation; error?: any } =
			await submitQuestionnaireResult({
				input: {
					questionnaireCode: 'meddibaby_end_questionnaire',
					userId: patientId,
					data: values,
				},
			});

		const result2 = await endHealthPlanUser({
			healthPlanUserId: healthPlanUser.id!,
			questionnaireResultId: result?.data?.submitQuestionnaireResult?.id,
		});

		setIsLoading(false);
		showNotificationSuccess({
			content: 'Zdravotní plán ukončen',
		});
		unregisterFormsByKey([TreatmentFormModalContextKeys.MEDDI_BABY_END_PLAN]);
		onSuccess();
	};

	useEffect(() => {
		registerFormByKey(
			TreatmentFormModalContextKeys.MEDDI_BABY_END_PLAN,
			() => formMethods,
		);
	}, []);

	return (
		<ScrollContainer>
			<div className='col-12 col-lg-8'>
				<div className='page-panel mt-1'>
					<FormWrapper formMethods={formMethods}>
						<DateField
							isRequired
							name='birthDate'
							label='base.birthDate'
							className={styles.field}
						/>
						<SelectField
							isRequired
							label='base.birthMethod'
							name='birthMethod'
							className={styles.field}
							options={BIRTH_METHOD_OPTIONS}
						/>
						<NumberField
							isRequired
							name='birthWeek'
							label='base.birthWeek'
							className={styles.field}
						/>
						<RadioListField
							isRequired
							label='base.birthOfChild'
							name='birthOfChild'
							className={styles.field}
							data={GENDER_RADIO_OPTIONS}
							inLine
						/>
						<NumberField
							isRequired
							label='base.newbornWeight'
							name='newbornWeight'
							className={styles.field}
							suffix='g'
						/>
						<NumberField
							label='base.newbornHeight'
							name='newbornHeight'
							className={styles.field}
							suffix='cm'
						/>
						<FormContainer name='apgar'>
							<span>Apgar skore</span>
							<div className={styles.apgarContainer}>
								<SelectField
									name='first'
									className={styles.field}
									options={APGAR_VALUES}
								/>
								<div className={styles.apgarSplitter}>-</div>
								<SelectField
									name='second'
									className={styles.field}
									options={APGAR_VALUES}
								/>
								<div className={styles.apgarSplitter}>-</div>
								<SelectField
									name='third'
									className={styles.field}
									options={APGAR_VALUES}
								/>
							</div>
						</FormContainer>
						<RadioListField
							isRequired
							label='base.courseOfChildBirthBaby'
							name='courseOfChildBirthBaby'
							className={styles.field}
							data={[
								{
									title: 'base.withoutComplications',
									value: PREGNANCY_COURSE.WITHOUT_COMPLICATIONS,
								},
								{
									title: 'base.complicated',
									value: PREGNANCY_COURSE.COMPLICATED,
								},
							]}
							inLine>
							<FormCondition
								controlName='courseOfChildBirthBaby'
								equal={PREGNANCY_COURSE.COMPLICATED}>
								<FormContainer settings={{ showLine: true }}>
									<InputField
										isRequired
										label='base.complicationsDescription'
										name='courseOfChildBirthBabyComplications'
										className={styles.field}
									/>
								</FormContainer>
							</FormCondition>
						</RadioListField>
						<RadioListField
							isRequired
							label='base.courseOfChildBirthMother'
							name='courseOfChildBirthMother'
							className={styles.field}
							data={[
								{
									title: 'base.withoutComplications',
									value: PREGNANCY_COURSE.WITHOUT_COMPLICATIONS,
								},
								{
									title: 'base.complicated',
									value: PREGNANCY_COURSE.COMPLICATED,
								},
							]}
							inLine>
							<FormCondition
								controlName='courseOfChildBirthMother'
								equal={PREGNANCY_COURSE.COMPLICATED}>
								<FormContainer settings={{ showLine: true }}>
									<InputField
										isRequired
										label='base.complicationsDescription'
										name='courseOfChildBirthMotherComplications'
										className={styles.field}
									/>
								</FormContainer>
							</FormCondition>
						</RadioListField>
						<InputField
							isRequired
							name='birthPlace'
							label='base.birthPlace'
							className={styles.field}
						/>
						<TextAreaField
							label='base.additionalInformation'
							name='birthAdditionalInformation'
							className={styles.field}
						/>
						<div className='d-flex mt-4 justify-content-between'>
							<SubmitButton
								title={'base.shared.save'}
								onSubmit={handleSubmit}
								disabled={isLoading}
							/>
						</div>
					</FormWrapper>
				</div>
			</div>
		</ScrollContainer>
	);
};

export default EndPlanSection;
